import React, { ComponentProps } from 'react';
import styled, { css } from 'styled-components';
import {
    Check12,
    Check16,
    LineHorizontal16,
    LineSingle12
} from '@honeybook/icons';
import { vars } from '../../theme';
import { transition } from '../../styles/animations';
import {
    focusState,
    hideInPrint,
    inlineFlexCenterAll
} from '../../styles/mixins';
import { hoverEase } from '../../styles/variables';
import {
    getControlStateStyles,
    CONTROL_COLORS
} from './selectionControlUtilities';
import type { HBCheckboxCustomTheme } from './HBCheckbox.types';
import MockPrintCheckboxButton from './MockPrintCheckboxButton';
import type { CSSDimensionValue, TControlSize } from '../../tokens/types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import type { MustIncludeOneOfTestIdProps } from '../common/internal/test-id.types';
import { isAriaInvalid } from '../common/internal/isAriaInvalid';

/* ------------------------------ Constants ------------------------------ */
export const SIZES = {
    sm: {
        width: '16px',
        height: '16px',
        borderRadius: '3px'
    },
    md: {
        width: '20px',
        height: '20px',
        borderRadius: '4px'
    },
    lg: {
        width: '24px',
        height: '24px',
        borderRadius: '3px'
    }
} as const;

const CHECKED_ICONS = {
    sm: Check12,
    md: Check16,
    lg: Check16
} as const;

const INDETERMINATE_ICONS = {
    sm: LineSingle12,
    md: LineHorizontal16,
    lg: LineHorizontal16
} as const;

/* ------------------------------ Types ------------------------------ */
export interface ICheckboxBase extends Omit<ComponentProps<'input'>, 'size'> {
    /** HTML name attribute forwarded to the underlying `<input>` element; used in form submission and Formik field binding. */
    name?: string;
    /** Size of the checkbox: 'sm' (16 px), 'md' (20 px), or 'lg' (24 px). Defaults to 'lg'. */
    size?: TControlSize;
    /** Overrides the checked-state background color; disabled state still falls back to surface.disabled. */
    customTheme?: HBCheckboxCustomTheme;
    /** Displays a minus icon instead of a checkmark; automatically cleared when `checked` is true. */
    indeterminate?: boolean;
    /** Whether the checkbox is checked. When used via HBFormCheckboxField, managed by Formik. */
    checked?: boolean;
    /** Fires when the checked state changes. Pair with `checked` to control the input. */
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    /** Disables user interaction and applies a not-allowed cursor. */
    disabled?: boolean;
    /** Applies error styling (red border) to the checkbox. When used via HBFormCheckboxField, managed by Formik. */
    error?: boolean;
    /** Accessible name for the checkbox when the field has no visible label (e.g. an `HBFieldControl` with `ariaLabel`). Ignored when `aria-labelledby` is set. Omitted: no attribute. */
    'aria-label'?: string;
    /** ID of the element that names the checkbox (e.g. an `HBFieldControl` label). Forwarded to the `<input>`. Omitted: no attribute. */
    'aria-labelledby'?: string;
    /** Space-separated IDs of hint/error elements describing the checkbox. Forwarded to the `<input>`. Omitted: no attribute. */
    'aria-describedby'?: string;
    /** Marks the checkbox invalid for assistive tech without error styling. `aria-invalid="true"` is set when this or `error` is true. Omitted: follows `error` only. */
    'aria-invalid'?: React.AriaAttributes['aria-invalid'];
    /** Suppresses hover and focus ring styles, useful for inline contexts like table bulk-select rows. */
    noFocusHover?: boolean;
    /** CSS margin applied to the checkbox wrapper element (e.g., `'0 8px'`). */
    margin?: CSSDimensionValue;
    /**
     * Fires on key press inside the checkbox input.
     * @deprecated Use `onKeyDown` instead.
     */
    onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    testId?: string;
}

export type HBCheckboxProps = ICheckboxBase & MustIncludeOneOfTestIdProps;

/* ------------------------------ Component ------------------------------ */
const HBCheckbox = ({
    id,
    name,
    testIdName,
    checked,
    disabled,
    size = 'lg',
    customTheme,
    onChange,
    className,
    onBlur,
    onKeyPress,
    testId,
    noFocusHover = false,
    tabIndex,
    indeterminate: _indeterminate = false,
    margin,
    error,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid
}: HBCheckboxProps) => {
    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName || id || name,
        suffix: 'checkbox',
        componentName: 'HBCheckbox'
    });

    const isInvalid = isAriaInvalid(error, ariaInvalid);
    const indeterminate = _indeterminate && !checked;
    const isActive = checked || indeterminate;
    const getIconColor = () => {
        if (error && !disabled) {
            return CONTROL_COLORS.error.icon;
        }
        return vars.colors.text.invertedPrimary;
    };

    return (
        <>
            <Checkbox
                isDisabled={disabled}
                size={size}
                $margin={margin}
                $customTheme={customTheme}
            >
                <Input
                    ref={el => {
                        if (el) {
                            el.indeterminate = indeterminate;
                        }
                    }}
                    data-testid={finalTestId}
                    type="checkbox"
                    id={id}
                    name={name}
                    checked={checked}
                    disabled={disabled}
                    $customTheme={customTheme}
                    onChange={onChange}
                    className={className}
                    onBlur={onBlur}
                    onKeyPress={onKeyPress}
                    tabIndex={tabIndex}
                    aria-checked={
                        checked ? 'true' : indeterminate ? 'mixed' : 'false'
                    }
                    aria-label={ariaLabelledBy ? undefined : ariaLabel}
                    aria-labelledby={ariaLabelledBy}
                    aria-describedby={ariaDescribedBy}
                    aria-invalid={isInvalid ? 'true' : undefined}
                    $isActive={isActive}
                    $error={error}
                    $isIndeterminate={indeterminate}
                    $noFocusHover={noFocusHover}
                />
                <IconWrapper $isActive={isActive} isDisabled={disabled}>
                    <Icon
                        size={size}
                        color={getIconColor()}
                        isIndeterminate={indeterminate}
                        isActive={isActive}
                    />
                </IconWrapper>
            </Checkbox>

            <MockPrintCheckboxButton
                size={size}
                color={customTheme?.background}
                isActive={isActive}
                margin={margin}
            >
                <Icon
                    size={size}
                    color={getIconColor()}
                    isIndeterminate={indeterminate}
                    isActive={isActive}
                />
            </MockPrintCheckboxButton>
        </>
    );
};

export default HBCheckbox;

/* ------------------------------ Checkbox Icon ------------------------------ */
const Icon = ({
    size,
    color,
    isIndeterminate,
    isActive
}: {
    size: TControlSize;
    color: string;
    isIndeterminate: boolean;
    isActive: boolean;
}) => {
    if (isIndeterminate) {
        const IconUnChecked = INDETERMINATE_ICONS[size];
        return <IconUnChecked color={color} />;
    }

    if (isActive) {
        const IconChecked = CHECKED_ICONS[size];
        return <IconChecked color={color} />;
    }

    return null;
};

const IconWrapper = styled.span<{
    $isActive: boolean;
    isDisabled: boolean;
}>`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    pointer-events: none;
    i {
        line-height: 0;
    }
`;

/* ------------------------------ INPUT ------------------------------ */
const Input = styled.input<{
    checked: boolean;
    $isActive: boolean;
    disabled: boolean;
    $error: boolean;
    $customTheme?: HBCheckboxCustomTheme;
    $isIndeterminate: boolean;
    $noFocusHover: boolean;
}>`
    cursor: pointer;
    appearance: none !important;
    margin: 0 !important;
    overflow: hidden;
    border-radius: inherit;
    border-width: 1px;
    border-style: solid;
    box-sizing: border-box;
    outline: none;
    ${transition('all', ...hoverEase)};
    ${({
        checked,
        disabled,
        $error,
        $customTheme,
        $isIndeterminate,
        $noFocusHover
    }) => {
        return getControlStateStyles({
            checked,
            disabled,
            error: $error,
            customTheme: $customTheme,
            isIndeterminate: $isIndeterminate,
            noFocusHover: $noFocusHover
        });
    }};
    ${focusState()}
`;

/* ------------------------------ Checkbox Styles ------------------------------ */
export const Checkbox = styled.div<{
    isDisabled: boolean;
    size: TControlSize;
    $margin?: CSSDimensionValue;
    $customTheme?: HBCheckboxCustomTheme;
}>`
    position: relative;
    display: inline-flex;
    align-items: center;
    ${({ isDisabled }) => isDisabled && 'pointer-events: none;'}
    ${inlineFlexCenterAll}

    &, input {
        ${props => SIZES[props.size]};
    }

    &&& input:disabled:checked {
        background: ${({ $customTheme }) =>
            $customTheme?.background || vars.colors.surface.disabled};
    }

    ${({ $margin }) =>
        $margin &&
        css`
            margin: ${$margin};
        `}

    ${hideInPrint}
`;
